
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { config } from '../config';

/**
 * HASH PASSWORD
 * Hashes a password using SHA-256
 */
export async function hashPassword(password) {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
}

/**
 * GET JWT PAYLOAD
 * Gets the payload from a JWT token
 */
export const getJwtPayload = (token) => {
    if (!token || typeof token !== 'string') {
        return null;
    }

    const parts = token.split('.');
    if (parts.length < 2) {
        return null;
    }

    try {
        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        const json = atob(padded);
        return JSON.parse(json);
    } catch (error) {
        console.error('Failed to decode JWT payload', error);
        return null;
    }
};

/**
 * CONVERT TO IST
 * Converts a date to IST
 */
export const convertToIST = (date) => {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    if (!date) return null;
    // Convert to IST (UTC+5:30)
    return dayjs.tz(date, "Asia/Kolkata").format("YYYY-MM-DDTHH:mm:ss");
};

/**
 * GET IMAGE URL
 * Handles prepending the API Base URL
 */
export const getImageUrl = (mediaObj) => {
    if (!mediaObj) return null;

    let path = null;

    // Accept either a direct string path or a media object
    if (typeof mediaObj === "string") {
        path = mediaObj;
    } else if (typeof mediaObj === "object") {
        path =
            mediaObj.file_path ||
            mediaObj.file ||
            mediaObj.url ||
            mediaObj.link;
    }

    // Never call startsWith() on a non-string value
    if (typeof path !== "string" || !path.trim()) {
        return null;
    }

    path = path.trim();

    if (path.startsWith("http")) return path;
    if (path.startsWith("data:")) return path;

    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    return `${config.uploadUrl}/${cleanPath}`;
};

/** Prefer banner media for homepage hero; fallback to first item. */
export const getFeaturedEventHeroImageUrl = (event) => {
    if (!event?.media?.length) return null;
    const loc = (m) => (m.file_location || '').toLowerCase();
    const banner = event.media.find((m) => loc(m) === 'banner');
    return getImageUrl(banner || event.media[0]);
};

/**
 * GET USER TYPE
 * Returns the type of user based on the JWT token (organizer if role includes "2", else user when logged in).
 */
export const getUserType = () => {
    const token = localStorage.getItem("authToken");

    if (!token) {
        return "guest";
    }

    const payload = getJwtPayload(token);
    const rawRole = payload?.role;

    const roles = Array.isArray(rawRole)
        ? rawRole
        : rawRole !== undefined && rawRole !== null
            ? [rawRole]
            : [];

    const normalizedRoles = roles
        .flatMap(role => String(role).split(","))
        .map(role => role.trim().toLowerCase());

    return (
        normalizedRoles.includes("2") ||
        normalizedRoles.includes("organizer")
    )
        ? "organizer"
        : "user";
};

/**
 * IS VALID EMAIL
 * Checks if the email is valid
 */
export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * IS VALID PHONE
 * Checks if the phone is valid
 */
export const isValidPhone = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
};

/**
 * IS STRONG PASSWORD
 * Checks if the password is strong
 */
export const isStrongPassword = (password) => {
    // At least 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
    const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return strongPasswordRegex.test(password);
};

/**
 * TO BASE64
 * Converts a file to Base64
 */
export const getBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

/**
 * FORMAT DATE TIME
 * Formats a date time string to "DD MMM YYYY | hh:mm A"
 */
export const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "";
    try {
      return dayjs(dateTimeString).format("DD MMM YYYY | hh:mm A");
    } catch {
      return dateTimeString;
    }
};